import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { getAuthToken, getCsrfToken, isAuthTokenExpiring, setAuthToken } from "../auth/cookies";
import { getApiBaseUrl } from "./baseUrl";
import { ApiError, ErrorResponse, handleAxiosError } from "./error-handler";
import type { IAuthTokenData } from "@/types/auth";
import type { IApiEnvelope } from "@/types/common";

const client = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 25000,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

async function tryRefreshSession(): Promise<boolean> {
  const csrf = getCsrfToken();
  try {
    const res = await client.post<IApiEnvelope<IAuthTokenData>>("/api/v1/auth/refresh", undefined, {
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
      skipErrorToast: true,
    });
    const data = res.data?.data;
    if (!data?.access_token) return false;
    setAuthToken(data.access_token, String(data.expires_in), data.user);
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return false;
    throw err;
  }
}

let refreshPromise: Promise<boolean> | null = null;

function refreshSessionOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = tryRefreshSession().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

client.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const isAuthCall = config.url?.includes("/api/v1/auth/") ?? false;
    if (getAuthToken() && !isAuthCall && isAuthTokenExpiring()) {
      try {
        await refreshSessionOnce();
      } catch {}
    }
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

client.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const isAuthCall = original?.url?.includes("/api/v1/auth/") ?? false;
    const envelopeCode = (error.response?.data as ErrorResponse | undefined)?.error?.code;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isAuthCall &&
      envelopeCode !== "ACCOUNT_DELETED"
    ) {
      original._retried = true;
      try {
        if (await refreshSessionOnce()) {
          return client(original);
        }
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return handleAxiosError(error as AxiosError<ErrorResponse>);
  },
);

export default client;
